import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Scrollbar, Text} from '@forecast-it/design-system';
import {Moment} from 'moment';

import InvoiceProjectsTable, {InvoiceProjectRow} from './invoice_projects_table';
import {TimeMaterialDateRange} from './TimeMaterialDateRange';
import {
	ProjectTablesContainer,
	ProjectTablesScrollArea,
	ProjectTablesWrapper,
} from '../../../../styles/v2/invoice_modals_v2_styled';
import {BUDGET_TYPE} from '../../../../constants';
import {UseInvoiceClientContacts} from '../useInvoiceClientContacts';

interface ProjectSection {
	projects: InvoiceProjectRow[];
}

interface InvoiceProjectTablesProps {
	onProjectSelect: (projectId: string) => void;
	onSelectAll: (projectIds: string[]) => void;
	selectedProjectsIds: string[];
	projectData:
		| {
				time_material?: ProjectSection;
				fixed_price?: ProjectSection;
				retainer?: ProjectSection;
		  }
		| null
		| undefined;
	isProjectInvoicing: boolean;
	contactsFeatureEnabled: boolean;
	getContacts: UseInvoiceClientContacts['getContacts'];
	getSelectedContact: UseInvoiceClientContacts['getSelectedContact'];
	setSelectedContact: UseInvoiceClientContacts['setSelectedContact'];
	showTimeMaterialSection: boolean;
	startDate: Moment;
	endDate: Moment;
	onDateRangeChange: (start: Moment, end: Moment) => void;
}

const InvoiceProjectTables = ({
	onProjectSelect,
	onSelectAll,
	selectedProjectsIds,
	projectData,
	isProjectInvoicing,
	contactsFeatureEnabled,
	getContacts,
	getSelectedContact,
	setSelectedContact,
	showTimeMaterialSection,
	startDate,
	endDate,
	onDateRangeChange,
}: InvoiceProjectTablesProps): JSX.Element => {
	const intl = useIntl();
	const hasTimeMaterialRows = !!projectData?.time_material?.projects?.length;

	return (
		<ProjectTablesContainer data-userpilot={'projects-tables'}>
			<ProjectTablesScrollArea>
				<Scrollbar hasFocusableContent>
					<ProjectTablesWrapper>
						<InvoiceProjectsTable
							projects={projectData?.time_material?.projects}
							budgetTypeName={intl.formatMessage({id: 'common.time_material'})}
							onProjectSelect={onProjectSelect}
							onSelectAll={onSelectAll}
							selectedProjectsIds={selectedProjectsIds}
							budgetType={BUDGET_TYPE.TIME_AND_MATERIALS}
							renderWhenEmpty={showTimeMaterialSection}
							headerContent={
								showTimeMaterialSection ? (
									<TimeMaterialDateRange
										startDate={startDate}
										endDate={endDate}
										onChange={onDateRangeChange}
									/>
								) : undefined
							}
							headerNote={
								showTimeMaterialSection ? (
									<Text size="2" color="auxiliary">
										<FormattedMessage
											id={
												hasTimeMaterialRows
													? 'invoicing.tm_date_range_note'
													: 'invoicing.tm_nothing_in_range'
											}
										/>
									</Text>
								) : undefined
							}
							isProjectInvoicing={isProjectInvoicing}
							contactsFeatureEnabled={contactsFeatureEnabled}
							getContacts={getContacts}
							getSelectedContact={getSelectedContact}
							setSelectedContact={setSelectedContact}
						/>
						<InvoiceProjectsTable
							projects={projectData?.fixed_price?.projects}
							budgetTypeName={intl.formatMessage({id: 'common.fixed_price'})}
							onProjectSelect={onProjectSelect}
							onSelectAll={onSelectAll}
							selectedProjectsIds={selectedProjectsIds}
							budgetType={BUDGET_TYPE.FIXED_PRICE_V2}
							isProjectInvoicing={isProjectInvoicing}
							contactsFeatureEnabled={contactsFeatureEnabled}
							getContacts={getContacts}
							getSelectedContact={getSelectedContact}
							setSelectedContact={setSelectedContact}
						/>
						<InvoiceProjectsTable
							projects={projectData?.retainer?.projects}
							budgetTypeName={intl.formatMessage({id: 'common.retainer'})}
							onProjectSelect={onProjectSelect}
							onSelectAll={onSelectAll}
							selectedProjectsIds={selectedProjectsIds}
							budgetType={BUDGET_TYPE.RETAINER}
							isProjectInvoicing={isProjectInvoicing}
							contactsFeatureEnabled={contactsFeatureEnabled}
							getContacts={getContacts}
							getSelectedContact={getSelectedContact}
							setSelectedContact={setSelectedContact}
						/>
					</ProjectTablesWrapper>
				</Scrollbar>
			</ProjectTablesScrollArea>
		</ProjectTablesContainer>
	);
};

export default InvoiceProjectTables;
